import { Button, Card, Col, Row, Space } from 'antd';
import { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';
import GrispiDateRangeInput, {
    PresetDateNameMap
} from '../../../../components/GrispiInput/components/GrispiDateRangeInput';
import GrispiInput from '../../../../components/GrispiInput/index';
import useCompanyTimeZone from '../../../../hooks/adminPage/useCompanyTimeZone';
import useUserById, { deriveCustomizedDetailedUser } from '../../../../hooks/useUserById';
import {
    parseSerializedValueFromUserFormValue,
    parseUserIdFromSerializedValue
} from '../../../../utils/forms/formHelper.parser';
import { generateReportQueryStringFromQuery } from '../TicketReport/TicketReportFiltersContainer';
enum CallFiltersQueryKeys {
    'startDate' = 'startDate',
    'endDate' = 'endDate',
    'agent' = 'agentId',
    'zone' = 'zone'
}
export enum CallFiltersQueryKeyMap {
    'startDate' = 'startDate',
    'endDate' = 'endDate',
    'agentId' = 'agentId',
    'zone' = 'zone'
}
export enum CallFiltersMandatoryQueryKeys {
    'startDate' = 'startDate',
    'endDate' = 'endDate',
    'zone' = 'zone'
}
enum CallReportFiltersTypeMap {
    AGENT = 'AGENT',
    DATE = 'DATE'
}

const CallReportFiltersContainer = () => {
    const { t } = useTranslation('reports');
    const { query, push } = useRouter();
    const { agentId, startDate, endDate } = query;
    const initialAgent = agentId !== undefined ? agentId?.toString() : null;
    const initialDate =
        startDate !== undefined && endDate !== undefined ? startDate?.toString() + '-' + endDate?.toString() : null;
    const { data } = useUserById(initialAgent);
    const agentData = deriveCustomizedDetailedUser(data);

    const agentValue = agentData ? `${agentData.id}|${agentData.fullName} (${agentData.primaryEmail.email})` : null;

    const { data: zone } = useCompanyTimeZone();

    const handleChange = (value: string | number, filterType: keyof typeof CallReportFiltersTypeMap | 'RESET') => {
        let queryString = '';
        switch (filterType) {
            case CallReportFiltersTypeMap.AGENT:
                queryString = generateReportQueryStringFromQuery(query, {
                    paramKey: CallFiltersQueryKeys[filterType.toLowerCase()],
                    paramValue: parseUserIdFromSerializedValue(parseSerializedValueFromUserFormValue(value?.toString()))
                });
                break;
            case CallReportFiltersTypeMap.DATE:
                queryString = generateReportQueryStringFromQuery(query, {
                    paramKey: CallReportFiltersTypeMap.DATE,
                    paramValue: value?.toString(),
                    zone
                });
                break;
            case 'RESET':
                queryString = generateReportQueryStringFromQuery(query, {
                    resetQuery: true
                });
                break;
            default:
                queryString = generateReportQueryStringFromQuery(query, {
                    paramKey: CallFiltersQueryKeys[filterType.toLowerCase()],
                    paramValue: value
                });
                break;
        }

        push(`/reports/call?${queryString}`);
    };

    return (
        <Space direction="vertical" className="full-width" size="small">
            <Card
                className="full-width"
                bodyStyle={{ padding: '12px' }}
                style={{ borderRadius: '5px', border: '1px solid rgba(0, 0, 0, 0.12)' }}
            >
                <Row gutter={[10, 5]} justify="space-between" className="full-width">
                    <Col span={24}>
                        <Row gutter={[10, 5]}>
                            <Col span={6}>
                                <GrispiDateRangeInput
                                    rangePickerProps={{ className: 'report-date-filter-input' }}
                                    onChange={(value) => handleChange(value, CallReportFiltersTypeMap.DATE)}
                                    value={initialDate}
                                    containerProps={{ label: t('callReports.dateSelection.label') }}
                                    presetDateRange={PresetDateNameMap.thisMonth}
                                />
                            </Col>
                            <Col span={6}>
                                <GrispiInput
                                    containerProps={{ label: t('callReports.agent.label') }}
                                    type="USER"
                                    antdProps={{ placeholder: t('callReports.agent.placeholder') }}
                                    isAgentInput={true}
                                    value={agentValue}
                                    onChange={(value) => handleChange(value, CallReportFiltersTypeMap.AGENT)}
                                    conditionInput
                                />
                            </Col>
                        </Row>
                        <Row>
                            <Space
                                className="full-width"
                                style={{
                                    display: 'flex',
                                    justifyContent: 'end',
                                    alignItems: 'end',
                                    marginTop: '10px'
                                }}
                            >
                                <Button
                                    type="primary"
                                    onClick={() => handleChange(null, 'RESET')}
                                    disabled={!initialAgent && !initialDate}
                                >
                                    {t('callReports.clearButtonLabel')}
                                </Button>
                            </Space>
                        </Row>
                    </Col>
                </Row>
            </Card>
        </Space>
    );
};

export default CallReportFiltersContainer;
